import {Component, OnInit, Input, ViewChild, ElementRef, HostListener} from '@angular/core';

import {Attachment} from '../../../models/attachment';

import {WatermarkService} from '../../watermark/watermark.service';

import * as _ from 'lodash';
import {ImageHelperService} from '../../image-helper/image-helper.service';
import {FileFactoryService} from '../../file-factory/file-factory.service';
import {TeamnoteConfigService} from '../../../configs/teamnote-config.service';
import {ModuleManagerService} from '../../../webclient/services/module/module-manager.service';
import {ModuleKeyDefinition} from '../../../constants/module.constant';
import {FileManagerService} from '../../file-manager/file-manager.service';
import {WebclientService} from '../../../webclient/webclient.service';
import {LoggerService} from '../../logger/logger.service';

@Component({
  selector: 'tn-attachment-image',
  templateUrl: './attachment-image.component.html',
  styleUrls: ['./attachment-image.component.scss']
})
export class AttachmentImageComponent implements OnInit {
  @Input() attachmentIds: string[] = [];
  @Input() defaultFileId: string = null;
  // Parallel to attachmentIds. Forwarded attachments reuse the same attachment_id,
  // so the id alone is not unique within a chat - the message_id is what tells
  // two copies of the same file apart.
  @Input() attachmentMessageIds: string[] = null;
  @Input() defaultMessageId: string = null;
  @Input() filename: string = null;
  @Input() filenameMap: object = {};
  @Input() isDisableWatermark = false;
  @Input() editImageCallbackWithImageUrlAndCaption: Function = null;
  @Input() isDisableDownload: boolean = false;
  @Input() isChatMessage: boolean = false;

  @ViewChild('imgCanvas', {static: false}) imgCanvas: ElementRef;
  @ViewChild('imageWatermarkCanvas', {static: true}) imageWatermarkCanvas: ElementRef;

  isAllowAttachmentSave: boolean = false;

  imgSrc: string;
  context: CanvasRenderingContext2D;
  image;

  realViewingImageDataUrl: string = null;
  originalImageDataUrl: string = null;
  currentCaption = '';
  currentAttachmentId = '';
  currentAttachmentMessageId: string = null;
  // The current image could not be fetched or decoded; shown instead of the
  // loading spinner so a failure does not look like an endless load.
  isLoadFailed = false;

  imageNum = 1;

  constructor(
    private _fileManagerService: FileManagerService,
    private _watermarkService: WatermarkService,
    private _imageHelperService: ImageHelperService,
    private _fileFactoryService: FileFactoryService,
    private _teamnoteConfigService: TeamnoteConfigService,
    private _moduleManagerService: ModuleManagerService,
    private _webclientService: WebclientService,
    private _loggerService: LoggerService,
  ) {
  }

  ngOnInit() {
  }

  ngOnChanges() {
    if (!this.attachmentIds) {
      return;
    }
    
    // default behaviour for encrypyed message download
    if (this.isChatMessage) {
      this.isAllowAttachmentSave = this._moduleManagerService.checkIfModuleExists(ModuleKeyDefinition.ATTACHMENT_SAVE) || this._moduleManagerService.checkIfModuleExists(ModuleKeyDefinition.ATTACHMENT_SAVE_WEB);
    
      //Always allow attachment download
      if (this._teamnoteConfigService.config.WEBCLIENT.CHATROOM.IS_ALWAYS_ALLOW_DOWNLOAD){
        this.isAllowAttachmentSave = true;
      }

      if (this._webclientService.allow_download_secure_message_webclient){
        this.isDisableDownload = false;
      }
    }

    if (this.defaultFileId) {
      const index = this.resolveDefaultIndex();
      if (index === -1) {
        // The clicked file is not in the list. Falling back to index 0 used to
        // open the chat's first image instead of the clicked one (TASC-1515),
        // so show the clicked file on its own rather than guess.
        this._loggerService.warn('[AttachmentImage] defaultFileId not found in attachmentIds, showing it alone: ' + this.defaultFileId);
        this.attachmentIds = [this.defaultFileId];
        this.attachmentMessageIds = this.defaultMessageId ? [this.defaultMessageId] : null;
        this.imageNum = 1;
      } else {
        this.imageNum = index + 1;
      }
    }

    this.prepareImage(this.imageNum);
    this.drawOverlayWatermark();
  }

  /**
   * Locate defaultFileId within attachmentIds.
   *
   * Forwarding an attachment re-sends the existing attachment_id instead of
   * re-uploading the file, so the same attachment_id can appear several times
   * in one chat. _.indexOf would then always resolve to the FIRST copy and the
   * viewer would open the wrong message's image. When the caller supplies the
   * parallel message_id array, match on (attachment_id, message_id) instead.
   *
   * - Message ids given and aligned: exact (attachment_id, message_id) match,
   *   or -1. Never fall back to another copy of the same file - that would show
   *   the other message's caption and filename date; -1 lets ngOnChanges show
   *   the clicked file on its own instead.
   * - No message ids given, or the arrays differ in length: id-only match, so
   *   callers that pass no message ids keep their previous behaviour.
   */
  private resolveDefaultIndex(): number {
    const hasMessageIds = !!this.defaultMessageId
      && !!this.attachmentMessageIds
      && this.attachmentMessageIds.length === this.attachmentIds.length;

    if (hasMessageIds) {
      return _.findIndex(
        this.attachmentIds,
        (id, i) => id === this.defaultFileId
          && String(this.attachmentMessageIds[i]) === String(this.defaultMessageId)
      );
    }

    return _.indexOf(this.attachmentIds, this.defaultFileId);
  }

  prepareImage(index: number): void {
    const attachmentId = this.attachmentIds[index - 1];
    // Set the current image before fetching: a cached or unrestricted file
    // calls back synchronously, and the stale-response check below reads it.
    this.currentAttachmentMessageId = this.attachmentMessageIds ? this.attachmentMessageIds[index - 1] : null;
    this.currentCaption = this._imageHelperService.getImageCaptionById(attachmentId, this.currentAttachmentMessageId);
    this.currentAttachmentId = attachmentId;

    this.realViewingImageDataUrl = null;
    this.isLoadFailed = false;
    // Only drawToCanvas sets this, and never for a GIF; left over, Edit would
    // hand the editor the previous image's pixels under this image's id.
    this.originalImageDataUrl = null;
    this._fileFactoryService.getFileByAttachmentId(
      attachmentId,
      (imgSrc, fileName) => {
        // Navigating quickly leaves earlier requests in flight; a slower one
        // finishing last would show its image under another image's counter.
        if (!this.isCurrentAttachment(attachmentId)) {
          return;
        }
        if (this._fileManagerService.checkIfAttachmentIsGif(attachmentId)) {
          this.realViewingImageDataUrl = imgSrc;
          return
        }

        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
          if (!this.isCurrentAttachment(attachmentId)) {
            return;
          }
          this.drawToCanvas(image, attachmentId);
        };
        image.onerror = () => {
          this.onImageLoadFailed(attachmentId);
        };
        image.src = imgSrc;
      },
      () => {
        this.onImageLoadFailed(attachmentId);
      },
      false
    );
  }

  /**
   * A failed fetch or decode of an image the user has already moved past is
   * ignored, like a stale success.
   */
  onImageLoadFailed(attachmentId: string): void {
    if (!this.isCurrentAttachment(attachmentId)) {
      return;
    }
    this.isLoadFailed = true;
  }

  // The GIF path hands the URL straight to the template <img>.
  onImgElementError(): void {
    this.onImageLoadFailed(this.currentAttachmentId);
  }

  /**
   * Compared by attachment_id rather than by request order on purpose: with
   * IS_RESTRICTED_ATTACHMENT on, FileFactoryService drops a repeat request for
   * an id that is still loading, so after going 1 -> 2 -> 1 only the FIRST
   * request for image 1 ever answers - with its result or, since a failure
   * now also clears the in-flight mark, its error. Discarding it as "old"
   * would leave the viewer loading forever. (Unrestricted files call back
   * synchronously.)
   */
  private isCurrentAttachment(attachmentId: string): boolean {
    return attachmentId === this.currentAttachmentId;
  }

  drawOverlayWatermark(): void {
    if (this._teamnoteConfigService.config.WEBCLIENT.WATERMARK.IS_DRAW_ON_IMAGE) {
      return;
    }
    if (this.isDisableWatermark) {
      return;
    }
    const watermarkCanvas: HTMLCanvasElement = this.imageWatermarkCanvas.nativeElement;
    const parent = watermarkCanvas.parentElement;
    const imgW = parent.clientWidth;
    const imgH = parent.clientHeight;
    watermarkCanvas.width = imgW;
    watermarkCanvas.height = imgH;
    const watermarkContext = watermarkCanvas.getContext('2d');
    this._watermarkService.drawWatermark(watermarkContext, imgW, imgH);
  }

  drawToCanvas(image, attachmentId?: string): void {
    // let canvas: HTMLCanvasElement = this.imgCanvas.nativeElement;
    const canvas: HTMLCanvasElement = document.createElement('canvas');

    const w = image.width;
    const h = image.height;

    // max 80vh & 80vw, *parent has padding:24px;
    // let maxH = window.innerHeight * 0.8;
    // let maxW = window.innerWidth * 0.8 - 48;

    // if (h > maxH) {
    //   w = w * (maxH / h);
    //   h = maxH;
    // }

    // if (w > maxW) {
    //   h = h * (maxW / w);
    //   w = maxW;
    // }

    canvas.width = w;
    canvas.height = h;
    this.context = canvas.getContext('2d');
    this.context.drawImage(image, 0, 0, w, h);
    this.originalImageDataUrl = canvas.toDataURL('image/jpeg');

    if (this._teamnoteConfigService.config.WEBCLIENT.WATERMARK.IS_DRAW_ON_IMAGE) {
      // draw on pic
      if (!this.isDisableWatermark) {
        this._watermarkService.drawWatermark(this.context, w, h);
      }
      this.realViewingImageDataUrl = canvas.toDataURL('image/jpeg');

      setTimeout(() => {
        // The user may have moved on during the delay.
        if (attachmentId && !this.isCurrentAttachment(attachmentId)) {
          return;
        }
        this.realViewingImageDataUrl = canvas.toDataURL('image/jpeg');
      }, 50);
    } else {
      // overlay
      this.realViewingImageDataUrl = this.originalImageDataUrl;
      // setTimeout(() => {
      //   let watermarkCanvas = this.imageWatermarkCanvas.nativeElement;
      //   let img = watermarkCanvas.nextElementSibling;
      //   let imgW = img.clientWidth;
      //   let imgH = img.clientHeight;
      //   watermarkCanvas.width = imgW;
      //   watermarkCanvas.height = imgH;
      //   let watermarkContext = watermarkCanvas.getContext("2d");
      //   this._watermarkService.drawWatermark(watermarkContext, imgW, imgH);
      // }, 100);
    }
  }

  prevImage() {
    if (this.imageNum <= 1) {
      return;
    }
    this.imageNum--;
    this.onImageIndexChange();
  }

  nextImage() {
    if (this.imageNum >= this.attachmentIds.length) {
      return;
    }
    this.imageNum++;
    this.onImageIndexChange();
  }

  onImageIndexChange() {
    if (!this.imageNum) {
      return;
    }
    if (this.imageNum < 1 || this.imageNum > this.attachmentIds.length) {
      return;
    }
    this.prepareImage(this.imageNum);
  }

  downloadImage(): void {
    const link = document.createElement('a');
    if (this.attachmentIds[0].includes('blob:http')) {
      link.download = this.filename;
      link.href = this.attachmentIds[0];
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      let fileName = null;
      if (!_.isEmpty(this.filenameMap)) {
        const attachmentKey = this._imageHelperService.buildAttachmentKey(this.currentAttachmentId, this.currentAttachmentMessageId);
        fileName = this.filenameMap[attachmentKey];
        if (!fileName) {
          // Callers that build the map without message ids still key by bare id.
          fileName = this.filenameMap[this.currentAttachmentId];
        }
      }

      if (!fileName) {
        const components = this.currentAttachmentId.split('.');
        const name = components[0];
        const extension = components[components.length - 1];
        fileName = `${name}.${(new Date()).toISOString()}.${extension}`;
      }

      this._fileManagerService.downloadFileByAttachmentId(this.currentAttachmentId, fileName);
    }
  }

  editImage(): void {
    // Nothing to edit while the image is loading, or for a GIF (not drawn to canvas).
    if (!this.originalImageDataUrl) {
      return;
    }
    if (this.editImageCallbackWithImageUrlAndCaption) {
      this.editImageCallbackWithImageUrlAndCaption(
        this.originalImageDataUrl,
        this.currentCaption,
        this.currentAttachmentId
      );
    }
  }

  @HostListener('window:keydown', ['$event']) handleKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowRight':
        this.nextImage();
        break;
      case 'ArrowLeft':
        this.prevImage();
        break;
    }
  }

  onRightClick() {
    // prevent download image via right click
    return this.isAllowAttachmentSave && !this.isDisableDownload;
  }

  // Save image
  // https://stackoverflow.com/questions/15931341/saving-canvas-locally-in-ie
}
